// Designing forms: new fields the Reader adds, written as AcroForm widgets with appearance streams (ISO 32000-1 §12.7).

import { PName, PRef } from "./parse";
import { serialize } from "./writer";
import { lit, num, pdfText } from "./form";

export type FieldType = "text" | "check" | "radio" | "combo" | "list" | "signature";
export type ValidationType = "none" | "number" | "integer" | "email" | "date" | "phone" | "regex" | "length";
export interface FieldValidation { type: ValidationType; min?: number; max?: number; pattern?: string; message?: string }

/** A form field the Reader is adding. Coordinates are PDF user space. Radio buttons with the same `name` form one group, told apart by `exportValue`. */
export interface FieldAnnot {
    id: string;
    kind: "field";
    subject?: string;
    page: number;
    rect: [number, number, number, number];
    ftype: FieldType;
    name: string;
    exportValue?: string;
    /** The question this field answers; kept as the tooltip. */
    label?: string;
    required: boolean;
    readOnly: boolean;
    multiline: boolean;
    options: string[];
    maxLen: number;
    validation?: FieldValidation;
    defaultValue?: string;
    fontSize?: number;
}

/** Checks `value` against a validation rule; the message to show when it fails, or null. */
export function validateValue(v: FieldValidation | undefined, value: string): string | null {
    if (!v || v.type === "none" || value === "") return null;
    const bad = (d: string) => v.message || d;
    switch (v.type) {
        case "number": case "integer": {
            const n = Number(value);
            if (!Number.isFinite(n) || (v.type === "integer" && !Number.isInteger(n))) return bad(v.type === "integer" ? "Enter a whole number." : "Enter a number.");
            if (v.min !== undefined && n < v.min) return bad(`The number must be at least ${v.min}.`);
            if (v.max !== undefined && n > v.max) return bad(`The number must be at most ${v.max}.`);
            return null;
        }
        case "email": return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : bad("Enter an email address.");
        case "phone": return /^[+()\-.\s\d]{6,20}$/.test(value) ? null : bad("Enter a phone number.");
        case "date": return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value)) ? null : bad("Enter a date as yyyy-mm-dd.");
        case "length":
            if (v.min !== undefined && value.length < v.min) return bad(`Enter at least ${v.min} characters.`);
            if (v.max !== undefined && value.length > v.max) return bad(`Enter at most ${v.max} characters.`);
            return null;
        case "regex":
            try { return new RegExp(v.pattern ?? "").test(value) ? null : bad("The answer is not in the expected format."); } catch { return null; }
    }
}

/** The same rule as Acrobat JavaScript, so other readers enforce it too. */
function validationJs(v: FieldValidation): string | null {
    const msg = JSON.stringify(v.message || "The answer is not valid.");
    const fail = `{app.alert(${msg});event.rc=false;}`;
    const pre = 'var v=event.value;if(v!==""&&';
    switch (v.type) {
        case "number": case "integer": return `${pre}(isNaN(v)${v.type === "integer" ? "||Number(v)%1!==0" : ""}${v.min !== undefined ? `||Number(v)<${v.min}` : ""}${v.max !== undefined ? `||Number(v)>${v.max}` : ""}))${fail}`;
        case "email": return `${pre}!/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(v))${fail}`;
        case "phone": return `${pre}!/^[+()\\-.\\s\\d]{6,20}$/.test(v))${fail}`;
        case "date": return `${pre}!/^\\d{4}-\\d{2}-\\d{2}$/.test(v))${fail}`;
        case "length": return `${pre}(${v.min !== undefined ? `v.length<${v.min}` : "false"}||${v.max !== undefined ? `v.length>${v.max}` : "false"}))${fail}`;
        case "regex": return v.pattern ? `${pre}!new RegExp(${JSON.stringify(v.pattern)}).test(v))${fail}` : null;
        default: return null;
    }
}

const ellipse = (cx: number, cy: number, rx: number, ry: number): string => {
    const k = 0.5523;
    return `${num(cx + rx)} ${num(cy)} m ${num(cx + rx)} ${num(cy + k * ry)} ${num(cx + k * rx)} ${num(cy + ry)} ${num(cx)} ${num(cy + ry)} c ${num(cx - k * rx)} ${num(cy + ry)} ${num(cx - rx)} ${num(cy + k * ry)} ${num(cx - rx)} ${num(cy)} c ${num(cx - rx)} ${num(cy - k * ry)} ${num(cx - k * rx)} ${num(cy - ry)} ${num(cx)} ${num(cy - ry)} c ${num(cx + k * rx)} ${num(cy - ry)} ${num(cx + rx)} ${num(cy - k * ry)} ${num(cx + rx)} ${num(cy)} c`;
};
const box = (w: number, h: number, round: boolean): string => {
    const style = "0.93 0.96 1 rg 0.3 0.45 0.8 RG 0.8 w";
    return round ? `${style} ${ellipse(w / 2, h / 2, w / 2 - 0.4, h / 2 - 0.4)} B\n` : `${style} 0.4 0.4 ${num(w - 0.8)} ${num(h - 0.8)} re B\n`;
};

export interface FieldWriteApi {
    newRef(): number;
    writeObj(num: number, body: string): void;
    writeStream(num: number, dict: string, data: Uint8Array, compress?: boolean): Promise<void>;
}

/** Writes the widgets of `fields` (all on one page); returns the page's new annotation references and the fields that go into /AcroForm /Fields. */
export async function writeFieldWidgets(fields: FieldAnnot[], pageRef: PRef, api: FieldWriteApi): Promise<{ widgets: PRef[]; top: PRef[] }> {
    const enc = new TextEncoder();
    const widgets: PRef[] = [];
    const top: PRef[] = [];
    const form = (n: number, w: number, h: number, content: string) => api.writeStream(n, `/Type /XObject /Subtype /Form /BBox [0 0 ${num(w)} ${num(h)}] /Resources << >>`, enc.encode(content));
    const groups = new Map<string, { parent: number; kids: number[]; v: string; required: boolean }>();

    for (const f of fields) {
        const [x0, y0, x1, y1] = f.rect;
        const w = x1 - x0, h = y1 - y0;
        const n = api.newRef();
        const flags = (f.readOnly ? 1 : 0) | (f.required ? 2 : 0);
        const common = `/Type /Annot /Subtype /Widget /Rect [${[x0, y0, x1, y1].map(num).join(" ")}] /P ${pageRef.num} ${pageRef.gen} R /F 4 /NM ${lit(f.id)} ${f.label ? `/TU ${lit(pdfText(f.label))} ` : ""}/MK << /BC [0.3 0.45 0.8] /BG [0.93 0.96 1] >> `;
        const js = f.validation && f.validation.type !== "none" ? validationJs(f.validation) : null;
        const extra = f.validation && f.validation.type !== "none" ? `/FGValidate ${lit(JSON.stringify(f.validation))} ${js ? `/AA << /V << /S /JavaScript /JS ${lit(js)} >> >> ` : ""}` : "";
        if (f.ftype === "check" || f.ftype === "radio") {
            const round = f.ftype === "radio";
            const on = round ? f.exportValue || "Choice" : f.exportValue || "Yes";
            const onName = serialize(new PName(on));
            const yes = api.newRef(), off = api.newRef();
            const dot = round
                ? `0.1 0.2 0.5 rg ${ellipse(w / 2, h / 2, Math.min(w, h) * 0.22, Math.min(w, h) * 0.22)} f\n`
                : `0.1 0.2 0.5 RG ${num(Math.min(w, h) * 0.14)} w 1 J 1 j ${num(w * 0.22)} ${num(h * 0.5)} m ${num(w * 0.42)} ${num(h * 0.25)} l ${num(w * 0.8)} ${num(h * 0.75)} l S\n`;
            await form(off, w, h, box(w, h, round));
            await form(yes, w, h, box(w, h, round) + dot);
            const checked = f.defaultValue === "on" || f.defaultValue === on;
            const ap = `/AP << /N << ${onName} ${yes} 0 R /Off ${off} 0 R >> >>`;
            if (round) {
                const g = groups.get(f.name) ?? { parent: api.newRef(), kids: [], v: "Off", required: false };
                g.kids.push(n);
                if (checked) g.v = on;
                if (f.required) g.required = true;
                groups.set(f.name, g);
                api.writeObj(n, `<< ${common}/Parent ${g.parent} 0 R /AS ${checked ? onName : "/Off"} ${ap} >>`);
            } else {
                api.writeObj(n, `<< ${common}/FT /Btn /T ${lit(pdfText(f.name))} /Ff ${flags} /V ${checked ? onName : "/Off"} /AS ${checked ? onName : "/Off"} ${extra}${ap} >>`);
                top.push(new PRef(n, 0));
            }
            widgets.push(new PRef(n, 0));
            continue;
        }
        const ap = api.newRef();
        await form(ap, w, h, box(w, h, false));
        if (f.ftype === "signature") {
            api.writeObj(n, `<< ${common}/FT /Sig /T ${lit(pdfText(f.name))} /Ff ${flags} /AP << /N ${ap} 0 R >> >>`);
        } else if (f.ftype === "combo" || f.ftype === "list") {
            const opt = f.options.map((o) => lit(pdfText(o))).join(" ");
            api.writeObj(n, `<< ${common}/FT /Ch /T ${lit(pdfText(f.name))} /Ff ${flags | (f.ftype === "combo" ? 131072 : 0)} /Opt [${opt}] /DA (/Helv ${num(f.fontSize ?? 0)} Tf 0 g) ${f.defaultValue ? `/V ${lit(pdfText(f.defaultValue))} ` : ""}${extra}/AP << /N ${ap} 0 R >> >>`);
        } else {
            api.writeObj(n, `<< ${common}/FT /Tx /T ${lit(pdfText(f.name))} /Ff ${flags | (f.multiline ? 4096 : 0)} /DA (/Helv ${num(f.fontSize ?? 0)} Tf 0 g) ${f.maxLen ? `/MaxLen ${f.maxLen} ` : ""}${f.defaultValue ? `/V ${lit(pdfText(f.defaultValue))} ` : ""}${extra}/AP << /N ${ap} 0 R >> >>`);
        }
        widgets.push(new PRef(n, 0));
        top.push(new PRef(n, 0));
    }
    for (const [name, g] of groups) {
        api.writeObj(g.parent, `<< /FT /Btn /Ff ${49152 | (g.required ? 2 : 0)} /T ${lit(pdfText(name))} /Kids [${g.kids.map((k) => `${k} 0 R`).join(" ")}] /V ${serialize(new PName(g.v))} >>`);
        top.push(new PRef(g.parent, 0));
    }
    return { widgets, top };
}
